import { useCallback, useEffect, useState } from "react";
import { cfFetch } from "../api/apiClient";
import type { Currency } from "../types";

export type ForecastScenario = "bad" | "mid" | "good";
export type RecurrenceType =
  | "average_last_x_months"
  | "yearly_percentage_growth"
  | "copy_last_month";

export type ForecastRuleCell = {
  id?: string;
  recurrenceType: RecurrenceType;
  lookbackMonths: number | null;
  yearlyGrowthRate: number | null;
  isActive: boolean;
  isDefault: boolean;
};

export type ForecastCategoryRuleRow = {
  categoryId: string;
  categoryName: string;
  categoryType: "income" | "expense";
  scenarios: Record<ForecastScenario, ForecastRuleCell>;
};

type UpsertPayload = {
  recurrenceType: RecurrenceType;
  lookbackMonths?: number | null;
  yearlyGrowthRate?: number | null;
  isActive?: boolean;
};

export function useForecastCategoryRules(displayCurrency: Currency) {
  const [rows, setRows] = useState<ForecastCategoryRuleRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await cfFetch(
        `/forecast/category-rules?displayCurrency=${encodeURIComponent(displayCurrency)}`,
      );
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.detail?.error || "Failed to load category rules");
      }
      const raw = (json?.rows || []) as ForecastCategoryRuleRow[];
      setRows(raw);
    } finally {
      setIsLoading(false);
    }
  }, [displayCurrency]);

  useEffect(() => {
    void load();
  }, [load]);

  const upsertRule = useCallback(
    async (
      categoryId: string,
      scenario: ForecastScenario,
      payload: UpsertPayload,
    ) => {
      setIsSaving(true);
      try {
        const res = await cfFetch(
          `/forecast/category-rules/${categoryId}/${scenario}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              recurrenceType: payload.recurrenceType,
              lookbackMonths: payload.lookbackMonths ?? null,
              yearlyGrowthRate: payload.yearlyGrowthRate ?? null,
              isActive: payload.isActive ?? true,
            }),
          },
        );
        const json = await res.json();
        if (!res.ok) {
          throw new Error(json?.detail?.error || "Failed to save category rule");
        }
        await load();
      } finally {
        setIsSaving(false);
      }
    },
    [load],
  );

  const clearRule = useCallback(
    async (categoryId: string, scenario: ForecastScenario) => {
      setIsSaving(true);
      try {
        const res = await cfFetch(
          `/forecast/category-rules/${categoryId}/${scenario}`,
          { method: "DELETE" },
        );
        if (!res.ok) {
          const json = await res.json();
          throw new Error(json?.detail?.error || "Failed to clear category rule");
        }
        await load();
      } finally {
        setIsSaving(false);
      }
    },
    [load],
  );

  return {
    rows,
    isLoading,
    isSaving,
    upsertRule,
    clearRule,
    refresh: load,
  };
}
